/// <reference path="../../typings/browser.d.ts" />
/// <reference path="../../customtypings/window.d.ts" />

/**
 * Connection Room service. This calls the backend (using other service like backend or videoFeed)
 */

namespace Application.Services {
    'use strict';

    import ConnectionRoom = Models.ConnectionRoom;
    import VideoFeed = Models.VideoFeed;
    import DeviceGui = Models.DeviceGui;
    import IPipe = Models.IPipe;
    import IHalList = Models.IHalList;
    import IHalElement = Models.IHalElement;
    import HalConverter = Models.HalConverter;
    import ConnRoomTakeHalResult = Application.Models.IConnRoomTakeHalResult;
    import ConnRoomTakeResult = Application.Models.ConnRoomTakeResult;

    export interface IConnectionsBundleIds {
        connectionBundleId: number,
        sinksConnectedToIdsLists?: number[][],
    }

    /**
     * ConnectionRoomService
     */
    export class ConnectionRoomService {
        public static $name: string = "connectionRoomService";
        public static $inject: string[] = ['$http', '$q', 'backendService', 'videoFeedService', '$rootScope'];

        private http: ng.IHttpService;
        private qService: ng.IQService;     
        private backendService: BackendService;
        private videoFeedService: VideoFeedService;
        private $rootScope: IFlexRootScope;

        private backendBaseUrl: string = window.backendUrl;
        // private servicePath: string = 'connrooms/';
        private servicePath: string = '';
        private connRoomsBackendUrl: string = this.backendBaseUrl + this.servicePath + 'connRooms';
        private pipesBackendUrl: string = this.backendBaseUrl + this.servicePath + 'pipes';
        private deviceGuiBackendUrl: string = this.backendBaseUrl + this.servicePath + 'devicegui';
        private takeUrlSuffix: string = "/take";
        private dropUrlSuffix: string = "/drop";
        private vtsForwardSuffix: string = "take/updateVtsForwardsBlocks/";

        constructor($http: ng.IHttpService,
                    $q: ng.IQService,
                    backendService: BackendService,
                    videoFeedService: VideoFeedService,
                    $rootScope: IFlexRootScope) {
            this.http = $http;
            this.qService = $q;
            this.backendService = backendService;
            this.videoFeedService = videoFeedService;
            this.$rootScope = $rootScope;
            this.$rootScope.vars = {
                isUserLoggedIn: false,
                connectionLocked: false,
            };
        }

        public getByHref(href: string, callback: (result: ConnectionRoom) => void): void {
            this.getConnRoomPromise(href)
                .then((element: ConnectionRoom) => {
                    callback(element);
                }
            );
        }

        public getAllConnectionRooms(callback: (messages: ConnectionRoom[]) => void ): void {
            this.getAllConnRoomsPromise()
                .then((elements: ConnectionRoom[]) => {
                    callback(elements);
                });
        }

        public getAllConnRoomsPromise(): ng.IPromise<ConnectionRoom[]> {
            return this.backendService
                .get(this.connRoomsBackendUrl + "/filtered", true)
                .then((result: ng.IHttpPromiseCallbackArg<IHalList<ConnectionRoom>>) => {
                    let halElement: IHalList<ConnectionRoom> = result.data;
                    let converted: ConnectionRoom[] = HalConverter.convertList(halElement, ConnectionRoom.Empty);
                    // let promises: ng.IPromise<ConnectionRoom>[] = converted.map((element: ConnectionRoom) => {
                    //         return this.fillPipes(element);
                    // });
                    // return this.qService.all(promises);
                    return converted;
                });
        }

        public createConnectionRoom(connRoom: ConnectionRoom): ng.IPromise<ConnectionRoom> {
            return this.backendService
                .post(this.connRoomsBackendUrl, {
                    name: connRoom.name,
                })
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<ConnectionRoom>>) => {
                    let halElement: IHalElement<ConnectionRoom> = result.data;
                    let converted: ConnectionRoom = HalConverter.convertElement(halElement, ConnectionRoom.Empty);
                    return converted;
                });
        }

        public updateConnectionRoom(connRoom: ConnectionRoom): ng.IPromise<ConnectionRoom> {
            return this.backendService
                .put(connRoom.href, {
                    name: connRoom.name,
                    creationDate: connRoom.creationDate,
                    blockHrefs: connRoom.blockHrefs,
                    defaultForOrganizationId: connRoom.defaultForOrganizationId,
                })
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<ConnectionRoom>>) => {
                    let halElement: IHalElement<ConnectionRoom> = result.data;
                    let converted: ConnectionRoom = HalConverter.convertElement(halElement, ConnectionRoom.Empty);
                    return this.fillPipes(converted);
                });
        }

        public removeConnectionRoom(connRoom: ConnectionRoom): ng.IPromise<{}> {
            return this.backendService.delete(connRoom.href);
        }

        public savePipe(pipe: IPipe): ng.IPromise<IPipe> {
            if (pipe.href && pipe.href !== '')
                return this.updatePipe(pipe);
            else
                return this.createPipe(pipe);
        }

        public changeStatus(pipe: IPipe, index: number): ng.IPromise<IPipe> {
            let destination: Models.IDestination = pipe.destinations[index];
            let url: string = pipe.href + (destination.status ? "/play" : "/pause");
            return this.http.put(url, index)
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IPipe>>) => {
                    let halElement: IHalElement<IPipe> = result.data;
                    let converted: IPipe = HalConverter.convertElement(halElement, Models.Pipe.Empty);
                    converted.connectionRoomHref = pipe.connectionRoomHref;
                    return converted;
            });
            
        }

        public removePipe(pipe: IPipe): ng.IHttpPromise<boolean> {
            return this.http.delete(pipe.href);
        }

        public take(connRoom: Models.IConnectionRoom, source: number, sink: number): ng.IPromise<ConnRoomTakeResult> {
            let url: string = connRoom.href + this.takeUrlSuffix;
            return this.backendService.put( url, {
                source: source,
                sink: sink,
            }).then((result: ng.IHttpPromiseCallbackArg<ConnRoomTakeHalResult>) => {
                let connTakeHalResult: ConnRoomTakeHalResult = result.data;
                let halElement: IHalElement<ConnectionRoom> = connTakeHalResult.connRoomResource;
                let converted: ConnectionRoom = HalConverter.convertElement(halElement, ConnectionRoom.Empty);
                HalConverter.selfConvertLinks(converted);
                return this.fillPipes(converted).then((connRoomRes: ConnectionRoom) => {
                    return new ConnRoomTakeResult(connRoomRes, connTakeHalResult.ok, connTakeHalResult.message);
                });
            });
        }

        public findVtsForwardBlocks(source: number, sink: number): ng.IPromise<any> {
            return this.backendService
                .put(this.backendBaseUrl + this.vtsForwardSuffix + source + '/' + sink, {})
                .then((result: any) => {
                    return result.data;
            });
        }

        public drop(connRoom: Models.IConnectionRoom, sink: number, isSwitch: boolean): ng.IPromise<ConnectionRoom> {
            const connRoomDrop: { sink: number, isSwitch: boolean } = {
                sink: sink,
                isSwitch: isSwitch,
            };
            return this.backendService.put(connRoom.href + this.dropUrlSuffix, connRoomDrop)
            .then((result: ng.IHttpPromiseCallbackArg<IHalElement<ConnectionRoom>>) => {
                let halElement: IHalElement<ConnectionRoom> = result.data;
                let converted: ConnectionRoom = HalConverter.convertElement(halElement, ConnectionRoom.Empty);
                return this.fillPipes(converted);
            });
        }

        public changeDestinationStatus(pipe: IPipe, destinationNum: number): ng.IPromise<IPipe> {
            pipe.destinations[destinationNum].status = !pipe.destinations[destinationNum].status;
            return this.updatePipe(pipe);
        }

        public disconnectDestination(pipe: IPipe, index: number): ng.IPromise<IPipe> {
            return this.backendService
                       .delete(pipe.href + "/destination/" + index)
                       .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IPipe>>) => {
                            let halElement: IHalElement<IPipe> = result.data;
                            let converted: IPipe = HalConverter.convertElement(halElement, Models.Pipe.Empty);
                            return converted;
                       });
        }

        public disconnectSource(pipe: IPipe): ng.IPromise<IPipe> {
            return this.backendService
                       .delete(pipe.href + "/source")
                       .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IPipe>>) => {
                            let halElement: IHalElement<IPipe> = result.data;
                            let converted: IPipe = HalConverter.convertElement(halElement, Models.Pipe.Empty);
                            return converted;
                       });
        }

        public addDestination(pipe: IPipe, destination: string, index: number): ng.IPromise<IPipe> {
            return this.backendService
                        .post(pipe.href + "/destination/" + index, destination)
                        .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IPipe>>) => {
                            let halElement: IHalElement<IPipe> = result.data;
                            let converted: IPipe = HalConverter.convertElement(halElement, Models.Pipe.Empty);
                            return converted;
                        });
        }

         public addSource(pipe: IPipe, source: string): ng.IPromise<IPipe> {
            return this.backendService
                        .post(pipe.href + "/source" , source)
                        .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IPipe>>) => {
                            let halElement: IHalElement<IPipe> = result.data;
                            let converted: IPipe = HalConverter.convertElement(halElement, Models.Pipe.Empty);
                            return converted;
                        });
        }

        /**
         * TODO: Modify backendService to be able to manage the success method
         */
        public findAvailableSources( callback: (blocks: Models.IBlock[]) => void): void {
            this.http
                .get(this.pipesBackendUrl + "/availableSources")
                .success((halElement: IHalList<Models.IBlock>) => {                    
                    let converted: Models.IBlock[] = HalConverter.convertList(halElement, Models.Block.Empty);
                    converted.forEach((block: Models.IBlock) => {
                        block.isSource = () =>  {
                            return true;
                        };
                    });
                    callback(converted);
                });
        }

        /**
         * TODO: Modify backendService to be able to manage the success method
         */
        public findAvailableSinks( callback: (blocks: Models.IBlock[]) => void): void {
            this.http
                .get(this.pipesBackendUrl + "/availableSinks")
                .success((halElement: IHalList<Models.IBlock>) => {
                    let converted: Models.IBlock[] = HalConverter.convertList(halElement, Models.Block.Empty);
                    converted.forEach((block: Models.IBlock) => {
                        block.isSource = () =>  {
                            return false;
                        };
                    });
                    callback(converted);
                });
        }

        /**
         * TODO: Move this to a videoFeedService
         */
        public getVideoFeedByBlockId(blockId: string, callback: (result: VideoFeed) => void): void {
            this.videoFeedService.getVideoFeedByBlockId(blockId)
                .then((element: VideoFeed) => {
                    callback(element);
                });
        }

        public getVideoFeedPromiseByBlockId(blockId: string): ng.IPromise<VideoFeed> {
            return this.videoFeedService.getVideoFeedByBlockId(blockId)
                .then((element: VideoFeed) => {
                    return element;
                });
        }

        public getVideoFeedByBlockIdNoList(blockId: string, callback: (result: VideoFeed) => void): void {
            this.videoFeedService.getVideoFeedByBlockIdNoList(blockId)
                .then((element: VideoFeed) => {
                    callback(element);
                });
        }

        /**
         * TODO: Move this to videoFeedService
         */
        public getVideoFeedPromiseByStreamPipeId(streamPipeId: string): ng.IPromise<VideoFeed> {
            return this.videoFeedService.getVideoFeedByStreamPipeId(streamPipeId)
                .then((element: VideoFeed) => {
                    return element;
                }
            );
        }

        public getVideoFeedPromiseByStreamPipeIdNoList(streamPipeId: string): ng.IPromise<VideoFeed> {
            return this.videoFeedService.getVideoFeedByStreamPipeIdNoList(streamPipeId)
                .then((element: VideoFeed) => {
                    return element;
                }
            );
        }

        /**
         * TODO: Move this and the related promises to a deviceGuiService
         */
        public getDeviceGuiByBlockId(blockId: string, callback: (result: DeviceGui) => void): void {
            this.getDeviceGuiByBlockIdPromise(blockId)
                .then((element: DeviceGui) => {
                    callback(element);
                });
        }

        public reorderPipes(connRoomHref: string, pipesOrder: number[]): void {
            this.backendService
                .post(connRoomHref + "/pipesOrder", { pipesOrder });
        }
        
        public validateConnection(sourceBlockId: number, sinkBlockId: number, prevSrc?: number): ng.IPromise<Models.IConnectionValidation> {
            let query: string = "";
            if (prevSrc !== undefined) {
                query = "?disconnectFrom=" + prevSrc;
            }
            return this.backendService
                .get(this.pipesBackendUrl + "/validateConnection/" + sourceBlockId + "/" + sinkBlockId + query).then(
                    (promiseValue: ng.IHttpPromiseCallbackArg<Models.IConnectionValidation>) => {
                        return promiseValue.data; }
                );
        }

        public cancelValidation(): void {
            this.backendService.delete(this.pipesBackendUrl + "/validateConnection");
        }

        /**
         * Call the backend to drop the locks of the session. if sourceBlockId and siknkBlockId are specified then it only frees
         * the locks linked with these blocksIds.
         * */
        public dropLock(sourceBlockId?: number, sinkBlockId?: number): void{
            if ((sourceBlockId !== undefined)&&(sinkBlockId !== undefined))
                this.backendService.delete(this.pipesBackendUrl + "/dropLock/"+ sourceBlockId + "/" + sinkBlockId );
            else
                this.backendService.delete(this.pipesBackendUrl + "/dropLock/");
        }

        public getConnRoomPromise(href: string): ng.IPromise<ConnectionRoom> {
            return this.backendService
                .get(href)
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<ConnectionRoom>>) => {
                    let halElement: IHalElement<ConnectionRoom> = result.data;
                    let converted: ConnectionRoom = HalConverter.convertElement(halElement, ConnectionRoom.Empty);
                    return this.fillPipes(converted);
                });
        }

        public getMatrixRoomSources(connRoom: Models.IConnectionRoom, 
                                    size?: number, 
                                    page?: number, 
                                    filteredCategories?: string[], 
                                    filteredLevel?: string): ng.IPromise<Models.IMatrixRoomInfo> {
                                    let url;
            url = connRoom.href + "/matrixRoom/sources";
           
            return this.getMatrixRoom(url, size, page, filteredCategories, filteredLevel);
        }

        public getMatrixRoomSinks(connRoom: Models.IConnectionRoom, 
                                  size?: number, 
                                  page?: number, 
                                  filteredCategories?: string[], 
                                  filteredLevel?: string): ng.IPromise<Models.IMatrixRoomInfo> {
                                    let url;
                url = connRoom.href + "/matrixRoom/sinks";
            return this.getMatrixRoom(url, size, page, filteredCategories, filteredLevel);
        }

        public fillPipes(connRoom: Models.ConnectionRoom): ng.IPromise<ConnectionRoom> {
            let url;
            url = connRoom.href + "/pipes";
           
            return this.backendService.get(url).then((nestedResult: ng.IHttpPromiseCallbackArg<IHalList<IPipe>>) => {
                let nestedHalElement: IHalList<IPipe> = nestedResult.data;
                let nestedConverted: IPipe[] = HalConverter.convertList(nestedHalElement, Models.Pipe.Empty);
                nestedConverted.forEach((pipe: IPipe) => {
                    pipe.connectionRoomHref = connRoom.href;
                });
                connRoom.pipes = nestedConverted;
                return connRoom;
            });
        }

        public getPipe(href: string): ng.IPromise<IPipe> {
            return this.backendService
                .get(this.pipesBackendUrl+ "/" + href)
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IPipe>>) => {
                    let halElement: IHalElement<IPipe> = result.data;
                    let converted: IPipe = HalConverter.convertElement(halElement, Models.Pipe.Empty);
                    return converted;
                });
        }

        public getMatrixRoomBlockConnections(blockId: number): ng.IPromise<Models.IConnectedBlock[]> {
            return this.backendService
                .get(this.connRoomsBackendUrl + "/matrixRoomBlocks/" + blockId)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IConnectedBlock[]>) => {
                return result.data;
            });
        }

        public getMatrixRoomBlockConnectionsLists(sinkBlocksIds: number[]): ng.IPromise<Models.IConnectedBlock[][]> {
            return this.backendService
                .post(this.connRoomsBackendUrl + "/matrixRoomBlocksList", sinkBlocksIds)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IConnectedBlock[][]>) => {
                    return result.data;
            });
        }

        private createPipe(pipe: IPipe): ng.IPromise<IPipe> {
            return this.backendService
                .post(this.pipesBackendUrl, {
                    connRoom: pipe.connectionRoomHref,
                    source: pipe.source,
                    destinations: pipe.destinations,
                    numOrder: pipe.numOrder,
                    name: pipe.name,
                })
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IPipe>>) => {
                    let halElement: IHalElement<IPipe> = result.data;
                    let converted: IPipe = HalConverter.convertElement(halElement, Models.Pipe.Empty);
                    return converted;
                });
        }

        private updatePipe(pipe: IPipe): ng.IPromise<IPipe> {
            return this.backendService
                .put(pipe.href, {
                    connRoom: pipe.connectionRoomHref,
                    source: pipe.source,
                    destinations: pipe.destinations,
                    numOrder: pipe.numOrder,
                    name: pipe.name,
                })
                .then((result: ng.IHttpPromiseCallbackArg<IHalElement<IPipe>>) => {
                    let halElement: IHalElement<IPipe> = result.data;
                    let converted: IPipe = HalConverter.convertElement(halElement, Models.Pipe.Empty);
                    converted.connectionRoomHref = pipe.connectionRoomHref;
                    return converted;
                });
        }

        /**
         * TODO: to study if backendService could be used here.
         */
        private getDeviceGuiByBlockIdPromise(blockId: string): ng.IPromise<DeviceGui> {
            return this.http
                .get(this.deviceGuiBackendUrl + "/" + blockId)
                .then((data: ng.IHttpPromiseCallbackArg<IHalElement<DeviceGui>>) => {
                    let halElement: IHalElement<DeviceGui> = data.data;
                    let converted: DeviceGui = HalConverter.convertElement(halElement, DeviceGui.Empty);
                    return converted;
                });
        }

        private getMatrixRoom(url: string, 
                              size?: number, 
                              page?: number, 
                              filteredCategories?: string[], 
                              filteredLevel?: string): ng.IPromise<Models.IMatrixRoomInfo> {
            let query: string = "?";
            if (size !== undefined) {
                query += "size=" + size;
            }
            if (page !== undefined) {
                query += "&page=" + page;
            }
            if (filteredCategories && filteredCategories.length > 0) {
                query += "&categories=" + filteredCategories.join(',');
            }
            if (filteredLevel) {
                query += "&level=" + filteredLevel;
            }
            return this.backendService
                .get(url + query)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IMatrixRoomInfo>) => {
                    let matrixRoom: Models.IMatrixRoomInfo = result.data;
                    matrixRoom.blocks.forEach((blockComplete: Models.IBlockComplete) => {
                        HalConverter.selfConvertLinks(blockComplete);
                        blockComplete.networkNode.type = blockComplete["nodeType"]; //tslint:disable-line
                        HalConverter.selfConvertLinks(blockComplete.networkNode);
                    // blockComplete.deviceGui.guiUrl = this.backendBaseUrl + blockComplete.deviceGui.guiUrl.substr(1);
                    });
                    return matrixRoom;
                });
        }

        public async reTake(source: number, sink: number, room: Models.ConnectionRoom): Promise<void>{
            await this
            .drop(room, sink, false)
            .then((res: any) => {
              return this
                .validateConnection(
                    source,
                    sink,
                  undefined
                )
                .then((validation: Models.IConnectionValidation) => {
                  if (
                    validation.result === 'OK' ||
                    validation.result === 'WARNING'
                  ) {
                    this.$rootScope.$broadcast('vars.connectionLocked', true);
                    return this
                      .take(
                        room,
                        source,
                        sink
                      );
                  }
                });
            });
        }
    }
}
